import { format, isToday } from 'date-fns'
import { useAppMutation } from '@/helpers/api/mutation'

import { createInstance } from '@/helpers/api/base'
import { useNotification } from '@/ui/Notification'
import { Log } from '@/types/logs'
import { DeleteModalProps, ModalTypes, useModal } from '@/components/Modal'
import { useGetCodeName } from '@/modules/CombinedLogs/hooks/useGetCodeName'
import { OFFICIAL_REPORT_TYPE_MAP } from '../constant'
import { useGetControllerLogParams } from '@/modules/CombinedLogs/apiHelpers/useGetControllerLogParams'

interface Props {
    log: Log
    callback?: () => void
}

export const useDeleteOfficialReport = ({ log, callback }: Props) => {
    const { showNotification } = useNotification()
    const { openModal } = useModal()
    const getCodeName = useGetCodeName()
    const logParams = useGetControllerLogParams()

    const deleteOfficialReportMutation = useAppMutation<string, Error, string, Log[]>({
        mutationFn: (reportId: string) =>
            createInstance({
                url: `/official-reports/${reportId}`,
                method: 'DELETE',
            }),
        alertOnError: true,
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: string, previousLogs?: Log[]) =>
            previousLogs
                ? previousLogs.map((item) =>
                      item.report && item.report.id === requestBody ? { ...item, report: null } : item
                  )
                : [],
        onSuccess: () => {
            showNotification({ message: 'Report was deleted' })
        },
    })

    const handleDeleteReport = ({ incidentDate, reportType }: { incidentDate: string; reportType: string }) => {
        const deleteFrom = isToday(incidentDate) ? 'today' : format(incidentDate, 'd/MM/y')
        const reportLabel =
            reportType in OFFICIAL_REPORT_TYPE_MAP ? OFFICIAL_REPORT_TYPE_MAP[reportType] : getCodeName(log.code)

        openModal<DeleteModalProps>(ModalTypes.deleteModal, {
            title: 'Delete report?',
            message: (
                <>
                    This will delete <strong>Official report - {reportLabel}</strong> from <strong>{deleteFrom}</strong>
                    . This cannot be undone.
                </>
            ),
            onDelete: () => {
                if (!log.report) {
                    return
                }

                if (callback) {
                    callback()
                } else {
                    deleteOfficialReportMutation.mutate(log.report.id)
                }
            },
        })
    }

    const performDelete = () => {
        if (!log.report) return

        if (callback) {
            callback()
        } else {
            deleteOfficialReportMutation.mutate(log.report.id)
        }
    }

    return {
        handleDeleteReport,
        performDelete,
    }
}
